(() => {
  const escapeHtml = (value) =>
    String(value || "").replace(
      /[&<>"']/g,
      (character) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[character],
    );
  const normalize = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const roleLabels = {
    antenna_base: "Works with",
    loading_coil: "Works with",
    radiator: "Works with",
    matching_unit: "Works with",
    mount: "Support option",
    mount_adapter: "Support option",
    mast: "Support option",
    radial_system: "RF return option",
    radial_accessory: "RF return option",
    feed_line: "Feed-line option",
    support: "Field support",
  };
  const explicit = {
    "cha-blank-adapter-1": [
      {
        handle: "cha-mcc-2-0",
        label: "Add loading and tuning",
        reason:
          "The MCC 2.0 installs on the CHA BLANK to create the documented loading and tuning section used by systems such as the CHA PRV 2.0.",
      },
      {
        handle: "stainless-steal-whip",
        label: "Choose a radiator",
        reason:
          "Add a documented CHA SS58, SS17, or SS25 stainless-steel whip when the complete-system guide calls for that radiator.",
      },
      {
        handle: "puck-hub",
        label: "Add an RF return hub",
        reason:
          "Use the CHA PUCK HUB where the documented configuration needs a central connection point for compatible radial wires.",
      },
      {
        handle: "carbon-fiber-tripod",
        label: "Add portable support",
        reason:
          "The compact tripod supports documented field configurations built around compatible Chameleon bases and accessories.",
      },
    ],
    "carbon-fiber-tripod": [
      {
        handle: "cha-blank-adapter-1",
        label: "Build a vertical feed point",
        reason:
          "The CHA BLANK provides the feed-point base for documented portable vertical systems supported by this tripod.",
      },
      {
        handle: "cha-hybrid-micro",
        label: "Build a compact broadband system",
        reason:
          "Pair the tripod with the documented HYBRID-MICRO path when a lightweight portable broadband deployment is the goal.",
      },
      {
        handle: "cha-hybrid-mini",
        label: "Build a broadband system",
        reason:
          "The HYBRID-MINI is a documented matching option for supported portable Chameleon configurations.",
      },
      {
        handle: "cha-mcc-2-0",
        label: "Build a tuned vertical",
        reason:
          "The MCC 2.0 is the loading and tuning section used in documented resonant vertical configurations supported from this field platform.",
      },
    ],
    "cha-cfm": [
      {
        handle: "cha-paracord-50-2-pack",
        label: "Add deployment line",
        reason:
          "Paracord provides the nonconductive field line used to raise, lower, and position the mast or supported wire system.",
      },
      {
        handle: "cha-hub",
        label: "Add the guying hub",
        reason:
          "The CHA HUB organizes the documented guying lines that stabilize the mast in the field.",
      },
      {
        handle: "cha-isolation-rings",
        label: "Protect wire deployments",
        reason:
          "Isolation rings provide documented attachment and strain-relief points for supported wire-antenna deployments.",
      },
      {
        handle: "universal-guying-system-ugs",
        label: "Add a guying system",
        reason:
          "The UGS supplies the field guying components needed to stabilize compatible portable mast deployments.",
      },
    ],
    "cha-cfm-34-hd": [
      {
        handle: "cha-surveyor-a-surveyor-tripod-adapter",
        label: "Use a surveyor tripod",
        reason:
          "The SURVEYOR-A provides the documented interface between compatible surveyor tripods and Chameleon support hardware.",
      },
      {
        handle: "cha-spike-mount",
        label: "Add ground support",
        reason:
          "The SPIKE-MOUNT provides a documented ground-based mounting option where the complete field configuration permits it.",
      },
      {
        handle: "cha-34hd-reducer-kit",
        label: "Adapt the mast tip",
        reason:
          "The 34HD REDUCER KIT adapts the CHA CFM-34 HD top section for documented compatible hardware.",
      },
      {
        handle: "universal-guying-system-ugs",
        label: "Stabilize the mast",
        reason:
          "The UGS supplies the guying components used to stabilize compatible portable mast deployments.",
      },
    ],
  };
  const productUrl = (handle) => `/products/${encodeURIComponent(handle)}`;
  const matchComponent = (components, handle, title) =>
    components.find((component) => component.shopify_handle === handle) ||
    components.find(
      (component) =>
        normalize(component.product_name).replace(/^cha /, "") ===
        normalize(title).replace(/^cha /, ""),
    );
  const relationshipLabel = (component, direct) =>
    direct
      ? "Documented relationship"
      : roleLabels[component.component_role] || "Documented option";
  const relationshipReason = (component, directRule, sharedCount) =>
    directRule
      ? directRule.conditions ||
        directRule.notes ||
        "A documented Chameleon relationship connects these products."
      : component.primary_benefit ||
        `Appears in ${sharedCount} documented complete-system path${sharedCount === 1 ? "" : "s"} with this product.`;
  const money = (cents) =>
    window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(cents)
      : new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency: window.Shopify?.currency?.active || "USD",
        }).format(Number(cents || 0) / 100);
  const fetchProduct = async (candidate) => {
    try {
      const response = await fetch(`${productUrl(candidate.handle)}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;
      return { ...candidate, product: await response.json() };
    } catch (_) {
      return null;
    }
  };
  const cardHtml = ({ product, label, reason }) => {
    const image = product.featured_image || product.images?.[0] || "";
    return `<article class="ckb-related-card"><a href="${escapeHtml(product.url || productUrl(product.handle))}"><div class="ckb-related-card__media">${image ? `<img src="${escapeHtml(image)}" loading="lazy" width="720" height="590" alt="${escapeHtml(product.title)}">` : ""}<span class="ckb-related-card__label">${escapeHtml(label)}</span></div><div class="ckb-related-card__body"><h3>${escapeHtml(product.title)}</h3><p class="ckb-related-card__reason">${escapeHtml(reason)}</p><div class="ckb-related-card__bottom"><span class="ckb-related-card__price">${money(product.price)}</span><span class="ckb-related-card__action">View product →</span></div></div></a></article>`;
  };
  const candidatesFor = (registry, handle, title) => {
    const records = registry.records;
    const component = matchComponent(records.components || [], handle, title);
    const scores = new Map();
    const add = (candidateHandle, score, label, reason) => {
      if (!candidateHandle || candidateHandle === handle) return;
      const previous = scores.get(candidateHandle);
      if (!previous || previous.score < score)
        scores.set(candidateHandle, {
          handle: candidateHandle,
          score,
          label,
          reason,
        });
    };
    for (const [index, candidate] of (explicit[handle] || []).entries())
      add(candidate.handle, 300 - index, candidate.label, candidate.reason);
    if (!component)
      return [...scores.values()].sort((a, b) => b.score - a.score);
    const componentsById = Object.fromEntries(
      records.components.map((item) => [item.component_id, item]),
    );
    for (const rule of records.compatibility_rules || []) {
      if (
        rule.source_component_id !== component.component_id &&
        rule.target_component_id !== component.component_id
      )
        continue;
      const otherId =
        rule.source_component_id === component.component_id
          ? rule.target_component_id
          : rule.source_component_id;
      const other = componentsById[otherId];
      if (other?.shopify_handle)
        add(
          other.shopify_handle,
          250,
          relationshipLabel(other, true),
          relationshipReason(other, rule, 1),
        );
    }
    const configurationIds = new Set(
      (records.configuration_components || [])
        .filter((link) => link.component_id === component.component_id)
        .map((link) => link.configuration_id),
    );
    const shared = new Map();
    for (const link of records.configuration_components || []) {
      if (
        !configurationIds.has(link.configuration_id) ||
        link.component_id === component.component_id
      )
        continue;
      shared.set(link.component_id, (shared.get(link.component_id) || 0) + 1);
    }
    for (const [componentId, count] of shared) {
      const other = componentsById[componentId];
      if (other?.shopify_handle)
        add(
          other.shopify_handle,
          100 + count,
          relationshipLabel(other, false),
          relationshipReason(other, null, count),
        );
    }
    return [...scores.values()].sort(
      (a, b) => b.score - a.score || a.handle.localeCompare(b.handle),
    );
  };
  const initializeCarousel = async (section) => {
    const registry = window.CKB_MPAS_DEPLOYMENT_INTELLIGENCE;
    if (!registry?.records) return;
    const limit = Math.max(2, Number(section.dataset.limit || 8));
    const candidates = candidatesFor(
      registry,
      section.dataset.productHandle,
      section.dataset.productTitle,
    ).slice(0, limit);
    if (!candidates.length) return;
    const products = (await Promise.all(candidates.map(fetchProduct))).filter(
      Boolean,
    );
    if (!products.length) return;
    const track = section.querySelector("[data-related-track]");
    const viewport = section.querySelector("[data-related-viewport]");
    const status = section.querySelector("[data-related-status]");
    const previous = section.querySelector("[data-related-previous]");
    const next = section.querySelector("[data-related-next]");
    track.innerHTML = products.map(cardHtml).join("");
    section.hidden = false;
    let index = 0;
    let timer = null;
    let manuallyPaused = false;
    const visibleCount = () =>
      window.matchMedia("(max-width: 767px)").matches
        ? 1
        : Math.min(3, products.length);
    const maxIndex = () => Math.max(0, products.length - visibleCount());
    const update = (announce = false) => {
      index = Math.min(maxIndex(), Math.max(0, index));
      const card = track.children[index];
      if (card)
        viewport.scrollTo({
          left: card.offsetLeft,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
        });
      previous.disabled = index === 0;
      next.disabled = index === maxIndex();
      if (announce)
        status.textContent = `Showing related product ${index + 1} of ${products.length}`;
    };
    const advance = () => {
      index = index >= maxIndex() ? 0 : index + 1;
      update(false);
    };
    const clear = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };
    const stop = () => {
      manuallyPaused = true;
      clear();
    };
    const start = () => {
      clear();
      const seconds = Number(section.dataset.autoplaySeconds || 7);
      if (
        !manuallyPaused &&
        seconds > 0 &&
        products.length > visibleCount() &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches
      )
        timer = window.setInterval(advance, seconds * 1000);
    };
    previous.addEventListener("click", () => {
      stop();
      index -= 1;
      update(true);
    });
    next.addEventListener("click", () => {
      stop();
      index += 1;
      update(true);
    });
    section.addEventListener("mouseenter", clear);
    section.addEventListener("focusin", clear);
    section.addEventListener("touchstart", stop, { passive: true });
    section.addEventListener("mouseleave", start);
    section.addEventListener("focusout", (event) => {
      if (!section.contains(event.relatedTarget)) start();
    });
    viewport.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      stop();
      index += event.key === "ArrowRight" ? 1 : -1;
      update(true);
    });
    window.addEventListener("resize", () => update(false));
    update(false);
    start();
  };
  const boot = () =>
    document
      .querySelectorAll("[data-ckb-related-products]")
      .forEach(initializeCarousel);
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
})();
